import { FilterIcon } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';

import { Button, Skeleton, Text } from '@/design-system/components/ui';

import { ContentContainer } from './components/ContentContainer';
import { SideBar } from './components/SideBar';

interface Props {
  title?: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  content?: React.ReactNode;
  sidebarTitle: string;
  sidebarContent?: React.ReactNode;
  filterCount?: number;
  isLoading?: boolean;
}

export const SidebarTemplate = ({
  title,
  subtitle,
  actions,
  sidebarTitle,
  content,
  sidebarContent,
  filterCount = 0,
  isLoading,
}: Props) => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [openSidebar, setOpenSidebar] = useState<boolean>(false);

  const handleFiltersToggle = useCallback((): void => {
    setOpenSidebar((prevState) => !prevState);
  }, []);

  const handleFiltersClose = useCallback((): void => {
    setOpenSidebar(false);
  }, []);

  return (
    <main className="tw:flex tw:flex-auto tw:overflow-hidden tw:relative">
      <div ref={rootRef} className="tw:absolute tw:inset-0 tw:flex">
        <SideBar
          title={sidebarTitle}
          container={rootRef.current}
          onClose={handleFiltersClose}
          open={openSidebar}
          sidebarContent={sidebarContent}
        />

        {/* Set "sidebar-template-scrolleable-container" id to match the functionality of InfiniteScroll component */}
        <ContentContainer id="sidebar-template-scrolleable-container" open={openSidebar}>
          <div className="tw:container tw:max-w-[1440px] tw:mx-auto tw:px-6 tw:pb-8">
            <div className="tw:flex tw:flex-col tw:gap-6 tw:animate-view-enter">
              <div className="tw:flex tw:items-start tw:flex-row tw:justify-between tw:gap-6 tw:animate-header-enter">
                <div className="tw:flex tw:flex-col tw:gap-2 tw:animate-title-enter">
                  {isLoading ? (
                    <>
                      <Skeleton variant="text" width={180} height={36} />
                      <Skeleton variant="text" width={240} height={20} />
                    </>
                  ) : (
                    <>
                      {title && (
                        <Text variant="display-sm" as="h3" className="tw:m-0">
                          {title}
                        </Text>
                      )}
                      {subtitle && (
                        <Text variant="body-md" color="muted">
                          {subtitle}
                        </Text>
                      )}
                    </>
                  )}
                </div>
                <div className="tw:flex tw:flex-row tw:gap-2">
                  <Button variant="ghost" size="sm" onClick={handleFiltersToggle}>
                    <FilterIcon size={20} />
                    {`Filters${filterCount > 0 ? ` (${filterCount})` : ''}`}
                  </Button>
                  {actions}
                </div>
              </div>
              {content}
            </div>
          </div>
        </ContentContainer>
      </div>
    </main>
  );
};
